import type { DayString, Profile, Repeat } from './types.js';

type Settings = Pick<Profile, 'timezone' | 'dayStartHour'>;

const DAY_MS = 86_400_000;

/** The user's local day for `now`, where a day starts at `dayStartHour`. */
export function localDay(now: Date, settings: Settings): DayString {
  const shifted = new Date(now.getTime() - settings.dayStartHour * 3_600_000);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: settings.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(shifted);
  const get = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function toUtc(day: DayString): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

export function addDays(day: DayString, n: number): DayString {
  return new Date(toUtc(day) + n * DAY_MS).toISOString().slice(0, 10);
}

export function daysBetween(from: DayString, to: DayString): number {
  return Math.round((toUtc(to) - toUtc(from)) / DAY_MS);
}

export function weekday(day: DayString): number {
  return new Date(toUtc(day)).getUTCDay();
}

export function isDueOn(repeat: Repeat, day: DayString): boolean {
  if (repeat.kind === 'weekly') return repeat.days.includes(weekday(day));
  const diff = daysBetween(repeat.startDate, day);
  return diff >= 0 && diff % repeat.every === 0;
}

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
